"use client";

import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight, FileText } from "lucide-react";

import { NavUser } from "@/components/nav-user";
import { ClientSwitcher } from "@/components/client-switcher";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

import { useGetUserQuery } from "api/institutional/user_api";
import {
  MenuOptions,
  AdminMenuOptions,
} from "@/components/NavSidebarIconMenu/MenuOptions";
import { Roles, Titles } from "@/interfaces/enums";
import { ReactComponent as Crosspoint } from "assets/svg/crosspoint-dark.svg";

import { ReactComponent as CrosspointSmall } from "assets/svg/crosspoint-logo-dark.svg";
import pack from "version.json";
import { Badge } from "./ui/badge";

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { data: user } = useGetUserQuery({});
  const { t } = useTranslation();
  const { isMobile, setOpenMobile } = useSidebar();
  const pathname = useLocation().pathname;
  const env =
    localStorage.getItem("app_env") ||
    process.env.REACT_APP_ENV ||
    "development";

  // Filter MenuOptions
  const mainNavItems = React.useMemo(() => {
    if (!user) return [];

    return MenuOptions.filter((option) => {
      if (option.hideOnNavBar) return false;
      if (option.showTo && option.showTo.length > 0) {
        const hasFlag = user.flags.some((flag: any) =>
          option.showTo.includes(flag),
        );
        if (!hasFlag) return false;
      }
      return true;
    }).map((option) => ({
      title:
        option.label || t(Titles[option.route as keyof typeof Titles] || ""),
      url: option.route,
      icon: option.icon,
      isActive: pathname === option.route,
    }));
  }, [user, pathname, t]);

  // Filter AdminMenuOptions
  const adminNavItems = React.useMemo(() => {
    if (!user || !user.is_admin) return [];

    return AdminMenuOptions.filter((option) => {
      if (option.role !== Roles.admin) return false;
      if (option.showTo && option.showTo.length > 0) {
        const hasFlag = user.flags.some((flag: any) =>
          option.showTo.includes(flag),
        );
        if (!hasFlag) return false;
      }
      return true;
    }).map((option) => ({
      title:
        option.label || t(Titles[option.route as keyof typeof Titles] || ""),
      url: option.route,
      icon: option.icon,
      isActive: pathname === option.route,
    }));
  }, [user, pathname, t]);

  const navigate = useNavigate();

  const handleClick = (url: string) => {
    if (isMobile) {
      setOpenMobile(false);
    }
    navigate(url);
  };

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader className="py-4">
        <div className="w-full text-center justify-center items-center hidden group-data-[collapsible=icon]:flex">
          <CrosspointSmall />
        </div>
        <div className="w-full text-center justify-center items-center flex group-data-[collapsible=icon]:hidden">
          <Crosspoint width={160} />
          {env !== "production" && (
            <Badge className="ms-1 capitalize" variant={"secondary"}>
              {env}
            </Badge>
          )}
        </div>
        <div className="pt-4">
          <ClientSwitcher />
        </div>
      </SidebarHeader>
      <SidebarContent>
        {/* Main Menu Group */}
        <SidebarGroup>
          <SidebarMenu>
            {mainNavItems.map((item) => (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton
                  asChild
                  tooltip={item.title}
                  isActive={item.isActive}
                  onClick={() => handleClick(item.url)}
                >
                  <div className="cursor-pointer py-5">
                    {item.icon && <item.icon />}
                    <span>{item.title}</span>
                  </div>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>

        {/* Admin Group - Collapsible */}
        {adminNavItems.length > 0 && (
          <Collapsible defaultOpen className="group/collapsible">
            <SidebarGroup>
              <SidebarGroupLabel asChild>
                <CollapsibleTrigger>
                  {t("Admin")}
                  <ChevronRight className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-90" />
                </CollapsibleTrigger>
              </SidebarGroupLabel>
              <CollapsibleContent>
                <SidebarMenu>
                  {adminNavItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        asChild
                        tooltip={item.title}
                        isActive={item.isActive}
                        onClick={() => handleClick(item.url)}
                      >
                        <div className="cursor-pointer py-5">
                          {item.icon && <item.icon />}
                          <span>{item.title}</span>
                        </div>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </CollapsibleContent>
            </SidebarGroup>
          </Collapsible>
        )}
      </SidebarContent>
      <SidebarFooter>
        <div className="">
          <a
            href="https://crosspoint.global/terminos"
            target="_blank"
            rel="noopener noreferrer"
            className="flex ps-2 items-center gap-2 text-xs text-zinc-100 hover:text-zinc-300 transition-colors group"
          >
            <FileText className="h-3.5 w-3.5 group-hover:text-zinc-300 transition-colors" />
            <span className="group-data-[collapsible=icon]:hidden">
              {t("Terms and Conditions")}
            </span>
            <span className="group-data-[collapsible=icon]:hidden">
              ({pack.version})
            </span>
          </a>
        </div>
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
